import React, { useState } from 'react';
import { useEditor } from '../../../contexts/EditorContext';
import { Trash2, ChevronUp, ChevronDown, Lock, Unlock, GripVertical } from 'lucide-react';

const LayersPanel = () => {
  const {
    slides,
    setSlides,
    activeSlide,
    setActiveSlide,
    selectedElements,
    setSelectedElements,
    deleteElement,
    toggleLock,
    reorderSlides,
    clearSlide,
  } = useEditor();

  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  const currentSlide = slides[activeSlide];
  const elements = currentSlide?.elements || [];
  
  const handleSelect = (id) => {
    setSelectedElements([id]);
  };
  
  const moveUp = (index) => {
    if (index >= elements.length - 1) return;
    
    const newElements = [...elements];
    [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
    
    // Update slides with new element order
    const newSlides = [...slides];
    newSlides[activeSlide].elements = newElements;
    setSlides(newSlides);
  };
  
  const moveDown = (index) => {
    if (index <= 0) return;
    
    const newElements = [...elements];
    [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
    
    // Update slides with new element order
    const newSlides = [...slides];
    newSlides[activeSlide].elements = newElements;
    setSlides(newSlides);
  };
  
  const getElementIcon = (type) => {
    switch (type) {
      case 'text':
        return '📝';
      case 'rect':
        return '⬜';
      case 'circle':
        return '⭕';
      case 'image':
        return '🖼️';
      default:
        return '📦';
    }
  };
  
  const getElementLabel = (element) => {
    if (element.type === 'text') {
      return element.text?.substring(0, 20) || 'Text';
    }
    if (element.type === 'image') {
      return 'Image';
    }
    return `${element.type.charAt(0).toUpperCase() + element.type.slice(1)}`;
  };
  
  return (
    <div className="flex-1 bg-white flex flex-col overflow-hidden">
      {/* Slides Section */}
      <div className="border-b border-gray-200">
        <div className="p-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Slides</h3>
            <p className="text-xs text-gray-400">{slides.length} slide{slides.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => {
              if (slides[activeSlide]?.elements.length === 0) return;
              if (window.confirm(`Clear all elements from Slide ${activeSlide + 1}? This can be undone.`)) {
                clearSlide();
              }
            }}
            disabled={slides[activeSlide]?.elements.length === 0}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
              slides[activeSlide]?.elements.length === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-red-500 hover:bg-red-50 hover:text-red-600'
            }`}
            title="Clear all elements from active slide"
          >
            Clear
          </button>
        </div>
        <div className="max-h-48 overflow-y-auto p-2 space-y-1">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              draggable
              onDragStart={(e) => {
                setDragIndex(index);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverIndex !== index) setDragOverIndex(index);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndex !== null && dragIndex !== index) {
                  reorderSlides(dragIndex, index);
                }
                setDragIndex(null);
                setDragOverIndex(null);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setDragOverIndex(null);
              }}
              onClick={() => {
                setActiveSlide(index);
                setSelectedElements([]);
              }}
              className={`w-full text-left px-2 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 select-none ${
                activeSlide === index
                  ? 'bg-blue-100 text-blue-800 border border-blue-300 font-medium'
                  : dragOverIndex === index && dragIndex !== index
                  ? 'bg-blue-50 border border-blue-300 text-gray-700'
                  : 'hover:bg-gray-100 text-gray-700 border border-transparent'
              } ${dragIndex === index ? 'opacity-40' : ''}`}
            >
              <GripVertical size={13} className="text-gray-350 shrink-0 cursor-grab" />
              <span className="w-5 h-5 rounded bg-gray-200 text-gray-600 text-xs flex items-center justify-center font-bold shrink-0">
                {index + 1}
              </span>
              <span className="truncate">{slide.name || `Slide ${index + 1}`}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Layers Section */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-3 bg-gray-50 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Layers</h3>
          <p className="text-xs text-gray-400">{elements.length} element{elements.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Layers List */}
        <div className="flex-1 overflow-y-auto p-2">
          {elements.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              No elements yet
            </div>
          ) : (
            <div className="space-y-1">
              {[...elements].reverse().map((element, reverseIndex) => {
                const actualIndex = elements.length - 1 - reverseIndex;
                const isSelected = selectedElements.includes(element.id);

                return (
                  <div
                    key={element.id}
                    onClick={() => handleSelect(element.id)}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                      isSelected
                        ? 'bg-blue-100 border border-blue-300'
                        : 'hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    <span className={`text-lg ${element.locked ? 'opacity-50' : ''}`}>{getElementIcon(element.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <p className={`text-sm font-medium truncate ${element.locked ? 'text-gray-400' : ''}`}>{getElementLabel(element)}</p>
                        {element.locked && <Lock size={10} className="text-amber-500 shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-500">{element.type}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleLock(element.id); }}
                        className={`p-1 rounded transition-colors ${
                          element.locked
                            ? 'text-amber-500 hover:bg-amber-50'
                            : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                        }`}
                        title={element.locked ? 'Unlock element' : 'Lock element'}
                      >
                        {element.locked ? <Lock size={13} /> : <Unlock size={13} />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveUp(actualIndex); }}
                        disabled={actualIndex >= elements.length - 1}
                        className={`p-1 rounded ${actualIndex >= elements.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-600'}`}
                        title="Move up"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveDown(actualIndex); }}
                        disabled={actualIndex <= 0}
                        className={`p-1 rounded ${actualIndex <= 0 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-600'}`}
                        title="Move down"
                      >
                        <ChevronDown size={14} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this element?')) deleteElement(element.id);
                        }}
                        className="p-1 rounded hover:bg-red-100 text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3 border-t border-gray-200 text-xs text-gray-400">
          <p>💡 Top = Front layer</p>
        </div>
      </div>
    </div>
  );
};

export default LayersPanel;
